import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdAttachFile } from 'react-icons/md';
import api from '../../utils/api';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { canFeatureForRole, PERMISSIONS } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { notifyApiError } from '../../utils/notify';
import { sortUsersByRole } from '../../utils/userSort';
import { getInitials } from '../../utils/getInitials';
import TaskReminderModal from '../../components/modals/TaskReminderModal';

// Max number of NEW files a user can upload at once. Only newly added (pending)
// files count toward this — existing attachments already on the task are ignored.
const MAX_NEW_ATTACHMENTS = 2;

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return new Date(date).toISOString().slice(0, 10);
  } catch (error) {
    return '-';
  }
};

const avatarStyle = {
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontSize: '11px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const UserMultiSelectDropdown = ({
  label,
  placeholder,
  users,
  selectedIds,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Live mirror of the selection, read by the ordering memo below without being one of its deps.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const filteredUsers = useMemo(() => {
    const value = search.trim().toLowerCase();
    const list = !value
      ? users
      : users.filter((user) =>
          `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
        );
    const sorted = sortUsersByRole(list);
    // Selected users float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selectedSet = new Set((selectedIdsRef.current || []).map((id) => String(id)));
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, open]);

  const selectedUsers = users.filter((user) => selectedIds.includes(user._id || user.id));

  const toggleUser = (userId) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter((id) => id !== userId));
      return;
    }
    onChange([...selectedIds, userId]);
  };

  const filteredIds = filteredUsers.map((user) => user._id || user.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange(selectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...filteredIds])]);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ fontSize: '13px', color: '#4a5568', marginBottom: '6px', fontWeight: 600 }}>{label}</div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          ...inputStyle,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedUsers.length > 0
            ? selectedUsers.map((user) => user.name).join(', ')
            : (placeholder || 'Nothing selected')}
        </span>
        <span style={{ flexShrink: 0, fontSize: '12px', color: '#718096' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={userDropdownPanelStyle}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <input
              placeholder={`Search ${(label || 'users').toLowerCase()}...`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
            {filteredUsers.length > 0 && (
              <button type="button" onClick={toggleSelectAll} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
            {filteredUsers.map((user) => {
              const userId = user._id || user.id;
              const checked = selectedIds.includes(userId);
              return (
                <label
                  key={userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 2px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f7fafc',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(userId)} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                      {user.name || 'Unnamed User'} ({user.role || '-'})
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TaskViewPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );
  const [loading, setLoading] = useState(true);
  const [taskData, setTaskData] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editingActualHour, setEditingActualHour] = useState(false);
  const [editingAssigneeHourUserId, setEditingAssigneeHourUserId] = useState('');
  const [assigneeHourDraft, setAssigneeHourDraft] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [projectDraft, setProjectDraft] = useState('');
  const [publicDraft, setPublicDraft] = useState(false);
  const [billableDraft, setBillableDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [actualHourDraft, setActualHourDraft] = useState('');
  const [assigneesDraft, setAssigneesDraft] = useState([]);
  const [followersDraft, setFollowersDraft] = useState([]);
  const [nameDraft, setNameDraft] = useState('');
  const [priorityDraft, setPriorityDraft] = useState('');
  const [categoryDraft, setCategoryDraft] = useState('');
  const [startDateDraft, setStartDateDraft] = useState('');
  const [dueDateDraft, setDueDateDraft] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [remindingTaskId, setRemindingTaskId] = useState('');
  const [reminderTask, setReminderTask] = useState(null);
  const [removingAttachmentId, setRemovingAttachmentId] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyOpenRef = useRef(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (_) {
      return {};
    }
  }, []);
  const currentUserId = String(currentUser?._id || currentUser?.id || '');
  const isAdminUser = useMemo(
    () => String(currentUser?.role || '').toLowerCase() === 'admin',
    [currentUser?.role]
  );
  const currentUserRole = String(currentUser?.role || '').toLowerCase();

  // Settings → User Roles matrix drives task editing affordances (matches backend enforcement).
  const { rolePermissions } = useRolePermissionMatrix();
  const canEditTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.EDIT_TASK]);

  useEffect(() => {
    setEditingAssigneeHourUserId('');
    setAssigneeHourDraft('');
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await api.getTask(taskId);
      if (response.success) {
        setTaskData(response.data);
        const t = response.data.task || {};
        const userId = String(currentUser?._id || currentUser?.id || '');
        const isAssignee = (t.assignees || []).some((u) => String(u?._id || u?.id) === userId);
        const ownRow = (t.actualHoursByUser || []).find((row) => String(row?.user?._id || row?.user || '') === userId);
        const draftActual = isAssignee ? Number(ownRow?.hour || 0) : Number(t.actualHour || 0);
        setDescriptionDraft(t.taskDescription || '');
        setStatusDraft(t.status || 'Not Started');
        setActualHourDraft(
          Number.isFinite(Number(draftActual)) ? String(draftActual) : '0'
        );
        setAssigneesDraft((t.assignees || []).map((user) => user._id || user.id));
        setFollowersDraft((t.followers || []).map((user) => user._id || user.id));
        setNameDraft(t.name || '');
        setPriorityDraft(t.priority || 'Medium');
        setCategoryDraft(t.taskCategory || 'Requirement');
        setStartDateDraft(t.startDate ? new Date(t.startDate).toISOString().slice(0, 10) : '');
        setDueDateDraft(t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 10) : '');
        setProjectDraft(String(response.data.project?._id || ''));
        setPublicDraft(Boolean(t.public));
        setBillableDraft(Boolean(t.billable));
      }
      const usersResponse = await api.getUsers({ limit: 1000, isActive: 'true' });
      if (usersResponse?.success) {
        setUsers(usersResponse.data || []);
      }
      const projectsResponse = await api.getProjects({ page: 1, limit: 500 });
      if (projectsResponse?.success) {
        setProjects(projectsResponse.data || []);
      }
    } catch (error) {
      notifyApiError(error, 'Failed to load task');
    } finally {
      setLoading(false);
      // Auto-refresh history if the section is open
      if (historyOpenRef.current) {
        loadTaskActivity();
      }
    }
  };

  const loadTaskActivity = async () => {
    try {
      setActivityLoading(true);
      const res = await api.getTaskActivity(taskId);
      if (res.success) setActivityLogs(res.data || []);
    } catch (_) {
      // silently ignore
    } finally {
      setActivityLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    historyOpenRef.current = next;
    setHistoryOpen(next);
    if (next && activityLogs.length === 0) {
      loadTaskActivity();
    }
  };

  const task = taskData?.task;
  const project = taskData?.project;

  const assignees = useMemo(() => task?.assignees || [], [task]);
  const followers = useMemo(() => task?.followers || [], [task]);
  // Display helper: a task may be assigned via the legacy single `assignedTo` field
  // with an empty `assignees` array. Surface both so the detail page matches what the
  // backend counts as "assigned" (Team Tasks / My Tasks match on assignees + assignedTo).
  const displayAssignees = useMemo(() => {
    const list = [...(task?.assignees || [])];
    const at = task?.assignedTo;
    if (at && !list.some((u) => String(u?._id || u?.id) === String(at?._id || at?.id))) {
      list.push(at);
    }
    return list;
  }, [task]);
  // The project currently selected in the edit form. Falls back to the task's
  // loaded project when the modal isn't open / hasn't been re-seeded yet.
  const selectedProject = useMemo(() => {
    if (!projectDraft) return null;
    return (
      projects.find((p) => String(p._id) === String(projectDraft)) ||
      (String(project?._id || '') === String(projectDraft) ? project : null)
    );
  }, [projectDraft, projects, project]);
  // Assignee/follower candidates depend on whether the task belongs to a project:
  //   • Inside a project  → only that project's members.
  //   • Individual task (No Project) → every user.
  const eligibleProjectUsers = useMemo(() => {
    if (!selectedProject) return users;
    const memberIds = new Set((selectedProject.members || []).map((member) => String(member?._id || member?.id)));
    if (!memberIds.size) return users;
    return users.filter((user) => memberIds.has(String(user._id || user.id)));
  }, [selectedProject, users]);
  // Users offered in the assignee/follower dropdowns. ONLY the project's eligible
  // members are selectable — anyone who isn't a member of the selected project
  // (e.g. an admin who was assigned before the task joined a project) is excluded
  // and gets stripped from the draft by the cleanup effect below on save.
  const assignableUsers = eligibleProjectUsers;
  const hasDescription = Boolean(task?.taskDescription && String(task.taskDescription).trim());
  const hasTags = Array.isArray(task?.tags) && task.tags.length > 0;
  const hasHourlyRate = Number(task?.hourlyRate || 0) > 0;
  const attachments = task?.attachments || [];
  const comments = task?.comments || [];
  const viewerIsAssignee = useMemo(() => {
    if (!currentUserId) return false;
    return assignees.some((user) => String(user?._id || user?.id) === currentUserId);
  }, [assignees, currentUserId]);
  const viewerOwnActualHour = useMemo(() => {
    if (!task || !currentUserId) return 0;
    const row = (task.actualHoursByUser || []).find(
      (entry) => String(entry?.user?._id || entry?.user || '') === currentUserId
    );
    return Number(row?.hour || 0);
  }, [task, currentUserId]);
  const totalActualHour = Number(task?.actualHour || 0);
  const displayedActualHour = viewerIsAssignee ? viewerOwnActualHour : Number(task?.actualHour || 0);

  useEffect(() => {
    if (!assignableUsers.length) {
      return;
    }

    const allowedIds = new Set(assignableUsers.map((user) => String(user._id || user.id)));
    const nextAssignees = assigneesDraft.filter((id) => allowedIds.has(String(id)));

    if (nextAssignees.length !== assigneesDraft.length) {
      setAssigneesDraft(nextAssignees);
    }
  }, [assignableUsers, assigneesDraft]);

  const formatHistoryFieldLabel = (field) => {
    const assigneePrefix = 'assigneeActualHour:';
    if (String(field || '').startsWith(assigneePrefix)) {
      const memberId = String(field).slice(assigneePrefix.length);
      const user = users.find((u) => String(u?._id || u?.id || '') === memberId);
      return `Assignee Actual Hours (${user?.name || 'Assignee'})`;
    }
    const map = {
      name: 'Task Name',
      status: 'Status',
      priority: 'Priority',
      taskCategory: 'Task Category',
      hour: 'Estimated Hours',
      actualHour: 'Total Actual Hours',
      yourActualHour: 'Your Actual Hours',
      taskDescription: 'Description',
      startDate: 'Start Date',
      dueDate: 'Due Date',
      assignees: 'Assignees',
      followers: 'Followers',
      public: 'Public',
      billable: 'Billable',
      projectId: 'Project',
      reminderRecipients: 'Reminder Sent To',
      reminderMessage: 'Reminder Message',
    };
    return map[field] || field;
  };

  const formatHistoryFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') return '(empty)';
    if (
      field === 'hour' ||
      field === 'actualHour' ||
      field === 'yourActualHour' ||
      String(field || '').startsWith('assigneeActualHour:')
    ) {
      const num = Number(value);
      if (!Number.isFinite(num)) return String(value);
      return `${num.toFixed(2)} h`;
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    // Project changes store the project id (or the literal "standalone"). Show a
    // human-readable project name instead of the raw ObjectId.
    if (field === 'projectId') {
      const raw = String(value);
      if (raw.toLowerCase() === 'standalone') return 'Standalone';
      const proj = projects.find((p) => String(p._id) === raw)
        || (String(project?._id || '') === raw ? project : null);
      return proj?.name || raw;
    }
    return String(value);
  };


  const onUploadFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    try {
      setUploading(true);
      await api.uploadTaskAttachments(taskId, files);
      toast.success('Attachment uploaded');
      fetchTask();
    } catch (error) {
      toast.error(error.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const saveTaskFields = async (payload, options = {}) => {
    const { showSuccessToast = true } = options;
    try {
      setSaving(true);
      await api.updateTask(taskId, payload);
      if (showSuccessToast) {
        toast.success('Task updated');
      }
      await fetchTask();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to update task');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Re-seed every draft from the current task, then open the single Edit Task modal.
  const openEditModal = () => {
    const t = task || {};
    setProjectDraft(String(project?._id || ''));
    setNameDraft(t.name || '');
    setStatusDraft(t.status || 'Not Started');
    setPriorityDraft(t.priority || 'Medium');
    setCategoryDraft(t.taskCategory || 'Requirement');
    setStartDateDraft(t.startDate ? new Date(t.startDate).toISOString().slice(0, 10) : '');
    setDueDateDraft(t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 10) : '');
    setAssigneesDraft((t.assignees || []).map((u) => u._id || u.id));
    setFollowersDraft((t.followers || []).map((u) => u._id || u.id));
    setDescriptionDraft(t.taskDescription || '');
    setPublicDraft(Boolean(t.public));
    setBillableDraft(Boolean(t.billable));
    setPendingAttachments([]);
    setCommentDraft('');
    setShowEditModal(true);
  };

  const handleModalSave = async () => {
    if (!nameDraft.trim()) {
      toast.warning('Task Name is required');
      return;
    }
    if (startDateDraft && dueDateDraft && new Date(dueDateDraft) < new Date(startDateDraft)) {
      toast.warning('Due date cannot be before the start date');
      return;
    }
    const ok = await saveTaskFields({
      projectId: projectDraft,
      name: nameDraft.trim(),
      status: statusDraft,
      priority: priorityDraft,
      taskCategory: categoryDraft,
      startDate: startDateDraft || undefined,
      dueDate: dueDateDraft || undefined,
      assignees: assigneesDraft,
      assignedTo: assigneesDraft[0] || undefined,
      followers: followersDraft,
      taskDescription: descriptionDraft,
      public: publicDraft,
      billable: billableDraft,
    });
    if (ok) {
      if (pendingAttachments.length > 0) {
        try {
          await api.uploadTaskAttachments(taskId, pendingAttachments);
          await fetchTask();
        } catch (uploadError) {
          toast.error(uploadError.message || 'Task saved, but attachment upload failed');
        }
      }
      if (commentDraft.trim()) {
        try {
          await api.addTaskComment(taskId, commentDraft.trim());
          await fetchTask();
        } catch (commentError) {
          toast.error(commentError.message || 'Task saved, but comment could not be added');
        }
      }
      setPendingAttachments([]);
      setCommentDraft('');
      setShowEditModal(false);
    }
  };

  // Open the reminder composer for the current task (edit message + pick recipients).
  const handleSendReminder = () => {
    setReminderTask(task);
  };

  // Normalise a task's assignees into { id, name, email } for the reminder modal.
  const getReminderAssignees = (task) => {
    const detail = Array.isArray(task?.assignees) ? task.assignees : [];
    const map = new Map();
    detail.forEach((a) => {
      const id = String(a?._id || a?.id || '');
      if (id) map.set(id, { id, name: a?.name || 'User', email: a?.email || '' });
    });
    return [...map.values()];
  };

  const submitReminder = async ({ note, recipientIds }) => {
    if (!taskId) return;
    try {
      setRemindingTaskId(taskId);
      const res = await api.sendTaskReminder(taskId, { note, recipientIds });
      toast.success(res?.message || 'Reminder sent');
      setReminderTask(null);
    } catch (error) {
      toast.error(error.message || 'Failed to send reminder');
    } finally {
      setRemindingTaskId('');
    }
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    try {
      setSubmittingComment(true);
      await api.addTaskComment(taskId, text);
      setCommentText('');
      toast.success('Comment added');
      fetchTask();
    } catch (error) {
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const onRemoveAttachment = async (attachmentId) => {
    if (!attachmentId) return;
    const confirmed = window.confirm('Remove this attachment?');
    if (!confirmed) return;
    try {
      setRemovingAttachmentId(attachmentId);
      await api.deleteTaskAttachment(taskId, attachmentId);
      toast.success('Attachment removed');
      fetchTask();
    } catch (error) {
      toast.error(error.message || 'Failed to remove attachment');
    } finally {
      setRemovingAttachmentId('');
    }
  };

  if (loading) {
    return <div style={{ padding: '16px' }}>Loading task...</div>;
  }

  if (!task) {
    return <div style={{ padding: '16px' }}>Task not found.</div>;
  }

  const statusBadge = STATUS_BADGE[task.status] || STATUS_BADGE['Not Started'];
  const priorityBadge = PRIORITY_BADGE[task.priority] || PRIORITY_BADGE.Medium;

  return (
    <div style={{ padding: '16px', background: '#f1f5f9', minHeight: '100%' }}>
      <div style={{ width: '100%', maxWidth: '1220px', margin: '0 auto' }}>
        {/* ── Header: task name (editable), meta pills, Back ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '24px', fontWeight: 700, color: '#2d3748', letterSpacing: '0.02em', lineHeight: 1.3 }}>
                #{task.taskCode || `T${String(task._id || taskId).slice(-4).toUpperCase()}`}
              </span>
              <span style={{ fontSize: '18px', fontWeight: 400, color: '#cbd5e0', lineHeight: 1.3 }}>—</span>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#2d3748', lineHeight: 1.3 }}>
                {task.name}
              </div>
            </div>
            {task?.featureId && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => project?._id && navigate(projectManagementPaths.featureView(project._id, task.featureId))}
                  style={{
                    ...metaPillStyle,
                    border: '1px solid #e2e8f0',
                    cursor: project?._id ? 'pointer' : 'default',
                  }}
                  disabled={!project?._id}
                  title={project?._id ? 'Open feature detail' : undefined}
                >
                  Feature:{' '}
                  <span style={{ color: '#2b6cb0', fontWeight: 700 }}>
                    {project?.feature?.name || project?.feature?.featureCode || 'Open Feature'}
                  </span>
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {canEditTaskPerm && (
              <button
                type="button"
                onClick={openEditModal}
                title="Edit task"
                style={{
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  height: '36px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'white',
                  padding: '0 16px',
                }}
              >
                ✏️ Edit Task
              </button>
            )}
            {canEditTaskPerm && assignees.length > 0 && (
              <button
                type="button"
                onClick={handleSendReminder}
                disabled={Boolean(remindingTaskId)}
                title="Notify assignees in-app and by email"
                style={{
                  border: '1px solid #c3dafe',
                  background: remindingTaskId ? '#ebf4ff' : '#eef2ff',
                  borderRadius: '8px',
                  height: '36px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: remindingTaskId ? 'not-allowed' : 'pointer',
                  color: '#4c51bf',
                  padding: '0 14px',
                }}
              >
                {remindingTaskId ? 'Sending…' : '🔔 Send Reminder'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                  return;
                }
                navigate(projectManagementPaths.tasks);
              }}
              style={{
                border: '1px solid #e2e8f0',
                background: '#fff',
                borderRadius: '8px',
                minWidth: '78px',
                height: '36px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                color: '#4a5568',
                padding: '0 12px',
              }}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* ── Two-column card grid: Task Info | Description + Comments ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '16px', alignItems: 'start' }}>
          {/* ── LEFT: Task Info card ── */}
          <div style={cardStyle}>
            <div style={cardTitleStyle}><span>🗂️</span> Task Info</div>

            {/* Project */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>📁</span> Project</div>
              {project?._id ? (
                <button
                  type="button"
                  onClick={() => navigate(projectManagementPaths.projectView(project._id))}
                  style={{ ...infoValueStyle, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: '#2b6cb0', fontWeight: 700 }}
                  title="Open project detail"
                >
                  {project?.name || '-'}
                </button>
              ) : (
                <span style={infoValueStyle}>{project?.name || '-'}</span>
              )}
            </div>

            {/* Status */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>🔵</span> Status</div>
              <span style={badgeStyle(statusBadge)}>{STATUS_DISPLAY_LABEL[task.status] || task.status || '-'}</span>
            </div>

            {/* Start Date */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>📅</span> Start Date</div>
              <span style={infoValueStyle}>{formatDate(task.startDate)}</span>
            </div>

            {/* Due Date */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>📅</span> Due Date</div>
              <span style={infoValueStyle}>{formatDate(task.dueDate)}</span>
            </div>

            {/* Priority */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>🚩</span> Priority</div>
              <span style={badgeStyle(priorityBadge)}>{task.priority || '-'}</span>
            </div>

            {/* Task Category */}
            <div style={infoRowStyle}>
              <div style={infoLabelStyle}><span>🏷️</span> Task Category</div>
              <span style={infoValueStyle}>{task.taskCategory || 'Requirement'}</span>
            </div>

            {hasHourlyRate && (
              <div style={infoRowStyle}>
                <div style={infoLabelStyle}><span>💲</span> Hourly Rate</div>
                <span style={infoValueStyle}>{`${task.hourlyRate || 0}.00`}</span>
              </div>
            )}
            {hasTags && (
              <div style={infoRowStyle}>
                <div style={infoLabelStyle}><span>🏷️</span> Tags</div>
                <span style={infoValueStyle}>{task.tags.join(', ')}</span>
              </div>
            )}

            {/* Assignees */}
            <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                <div style={{ ...infoLabelStyle, marginBottom: '8px' }}><span>👤</span> Assignees</div>
                {displayAssignees.length > 0 ? (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {displayAssignees.map((user) => (
                      <div key={user._id || user.id} title={user.name} style={avatarStyle}>
                        {getInitials(user.name)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#718096' }}>No assignees</div>
                )}
              </div>

            {/* Followers */}
            <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                <div style={{ ...infoLabelStyle, marginBottom: '8px' }}><span>👥</span> Followers</div>
                {followers.length > 0 ? (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {followers.map((user) => (
                      <div key={user._id || user.id} title={user.name} style={avatarStyle}>
                        {getInitials(user.name)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#718096' }}>No followers</div>
                )}
              </div>

            {/* Attachments */}
            <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
              <div style={{ ...infoLabelStyle, marginBottom: '8px' }}><MdAttachFile size={15} color="#1a202c" style={{ verticalAlign: '-3px' }} /> Attachments</div>
              <label
                style={{
                  minHeight: '110px',
                  border: '1px dashed #cbd5e0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3182ce',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '10px',
                  textAlign: 'center',
                  padding: '10px',
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  onUploadFiles(event.dataTransfer.files);
                }}
              >
                {uploading ? 'Uploading...' : 'Drop files here or click to attach documents'}
                <input
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(event) => onUploadFiles(event.target.files)}
                />
              </label>
              {attachments.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gap: '6px',
                    // Show ~4 rows, then scroll the rest inside the list (vertical only).
                    maxHeight: attachments.length > 4 ? '208px' : 'none',
                    overflowY: attachments.length > 4 ? 'auto' : 'visible',
                    overflowX: 'hidden',
                    paddingRight: attachments.length > 4 ? '4px' : 0,
                  }}
                >
                  {attachments.map((file) => (
                    <div
                      key={file._id || file.url}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: '#2b6cb0',
                          fontSize: '13px',
                          fontWeight: 600,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.fileName}
                      </a>
                      {file._id && (
                        <button
                          type="button"
                          onClick={() => onRemoveAttachment(file._id)}
                          disabled={removingAttachmentId === String(file._id)}
                          title="Remove attachment"
                          aria-label="Remove attachment"
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            border: '1px solid #fed7d7',
                            background: '#fff5f5',
                            color: '#c53030',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: removingAttachmentId === String(file._id) ? 'not-allowed' : 'pointer',
                            opacity: removingAttachmentId === String(file._id) ? 0.6 : 1,
                            flexShrink: 0,
                          }}
                        >
                          {removingAttachmentId === String(file._id) ? '…' : '🗑'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Description + Comments card ── */}
          <div style={cardStyle}>
            <div style={{ ...cardTitleStyle, marginBottom: '8px' }}><span>📝</span> Description</div>
            <div style={{
              background: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px 14px',
              minHeight: '160px',
            }}>
              {hasDescription ? (
                <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {task.taskDescription}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#718096' }}>No description</div>
              )}
            </div>

            <Section title="Comments" icon="💬">
              {canEditTaskPerm && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Your comment.."
                  style={{ ...inputStyle, height: '110px', paddingTop: '10px', resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={submitComment} disabled={submittingComment} style={smallActionBtnStyle('#3182ce', 'white')}>
                    {submittingComment ? 'Posting...' : 'Comment'}
                  </button>
                </div>
              </div>
              )}
              <div>
              {comments.length > 0 ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {comments.map((comment) => (
                    <div key={comment._id || `${comment.commentedAt}-${comment.text}`} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                        {comment.commentedBy?.name || 'User'}
                        <span style={{ marginLeft: '8px', color: '#718096', fontWeight: 400, fontSize: '12px' }}>
                          {formatDate(comment.commentedAt)}
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#4a5568', marginTop: '4px' }}>{comment.text}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#718096' }}>No comments yet</div>
              )}
              </div>
            </Section>
          </div>
        </div>

        {/* ── Single Edit Task modal (edits all Task Info fields at once) ── */}
        {showEditModal && (
          <div
            style={modalOverlayStyle}
          >
            <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: '#2d3748' }}>Edit Task</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    setShowEditModal(false);
                    setPendingAttachments([]);
                  }}
                  style={modalCloseBtnStyle}
                >×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
                <div>
                  <div style={fieldLabelStyle}>Project</div>
                  <select value={projectDraft} onChange={(e) => setProjectDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                    <option value="">No Project</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabelStyle}>Task Name</div>
                  <input
                    placeholder="Task Name"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                </div>
                <div>
                  <div style={fieldLabelStyle}>Status</div>
                  <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                    {TASK_STATUSES.map((status) => (
                      <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabelStyle}>Priority</div>
                  <select value={priorityDraft} onChange={(e) => setPriorityDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                    {PRIORITIES.map((priority) => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabelStyle}>Task Category</div>
                  <select value={categoryDraft} onChange={(e) => setCategoryDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                    {TASK_CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabelStyle}>Start Date</div>
                  <input type="date" value={startDateDraft} onChange={(e) => setStartDateDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                </div>
                <div>
                  <div style={fieldLabelStyle}>Due Date</div>
                  <input type="date" min={startDateDraft || undefined} value={dueDateDraft} onChange={(e) => setDueDateDraft(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                </div>
                <UserMultiSelectDropdown
                  label="Assignees"
                  placeholder="Select assignees"
                  users={assignableUsers}
                  selectedIds={assigneesDraft}
                  onChange={setAssigneesDraft}
                />
                <UserMultiSelectDropdown
                  label="Followers"
                  placeholder="Select followers"
                  users={users}
                  selectedIds={followersDraft}
                  onChange={setFollowersDraft}
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={fieldLabelStyle}>Task Description</div>
                <textarea
                  placeholder="Add Description"
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  style={{ ...inputStyle, width: '100%', height: '90px', paddingTop: '10px', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={fieldLabelStyle}>Comment</div>
                <textarea
                  placeholder="Add a comment"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  style={{ ...inputStyle, width: '100%', height: '70px', paddingTop: '10px', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={fieldLabelStyle}>Attachments</div>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      setPendingAttachments((prev) => {
                        // Only newly added files count toward the limit; existing
                        // attachments already on the task are ignored.
                        const remainingSlots = MAX_NEW_ATTACHMENTS - prev.length;
                        if (remainingSlots <= 0) {
                          toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                          return prev;
                        }
                        if (files.length > remainingSlots) {
                          toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                        }
                        return [...prev, ...files.slice(0, remainingSlots)];
                      });
                    }
                    e.target.value = '';
                  }}
                  style={{ ...inputStyle, width: '100%', height: 'auto', padding: '8px' }}
                />
                {pendingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                    {pendingAttachments.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          color: '#4a5568',
                          background: '#f7fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '4px 8px',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== index))}
                          style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer', fontWeight: 700 }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                {attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                    {attachments.map((file) => (
                      <div
                        key={file._id || file.url}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          background: '#ebf8ff',
                          border: '1px solid #bee3f8',
                          borderRadius: '6px',
                          padding: '4px 8px',
                        }}
                      >
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: 'none',
                            color: '#2b6cb0',
                            fontWeight: 600,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={file.fileName}
                        >
                          <MdAttachFile size={14} color="#1a202c" style={{ verticalAlign: '-3px', marginRight: '2px' }} />{file.fileName}
                        </a>
                        {file._id && (
                          <button
                            type="button"
                            aria-label={`Remove ${file.fileName}`}
                            title="Remove attachment"
                            onClick={() => onRemoveAttachment(file._id)}
                            disabled={removingAttachmentId === String(file._id)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#e53e3e',
                              cursor: removingAttachmentId === String(file._id) ? 'not-allowed' : 'pointer',
                              opacity: removingAttachmentId === String(file._id) ? 0.6 : 1,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >{removingAttachmentId === String(file._id) ? '…' : '🗑'}</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setPendingAttachments([]);
                  }}
                  style={smallActionBtnStyle('#fff', '#4a5568')}
                >Cancel</button>
                <button
                  type="button"
                  onClick={handleModalSave}
                  disabled={saving}
                  style={{
                    ...smallActionBtnStyle('#3182ce', 'white'),
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                  }}
                >
                  {saving ? 'Saving...' : 'Update Task'}
                </button>
              </div>
            </div>
          </div>
        )}

        <TaskReminderModal
          isOpen={Boolean(reminderTask)}
          taskName={reminderTask?.name}
          assignees={reminderTask ? getReminderAssignees(reminderTask) : []}
          currentUserId={currentUserId}
          sending={Boolean(remindingTaskId)}
          onClose={() => {
            if (!remindingTaskId) setReminderTask(null);
          }}
          onSubmit={submitReminder}
        />

        {/* ── Collapsible Task History card ─────────────────────────── */}
        <div style={{ ...cardStyle, marginTop: '16px', padding: '4px 20px' }}>
          <button
            type="button"
            onClick={toggleHistory}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#2d3748',
            }}
          >
            <span style={cardTitleStyle}><span>🕐</span> Task History</span>
            <span style={{ fontSize: '18px', color: '#718096', lineHeight: 1 }}>
              {historyOpen ? '▲' : '▼'}
            </span>
          </button>

          {historyOpen && (
            <div style={{ paddingBottom: '20px' }}>
              {activityLoading ? (
                <div style={{ color: '#a0aec0', fontSize: '13px', padding: '12px 0' }}>Loading…</div>
              ) : activityLogs.length === 0 ? (
                <div style={{ color: '#a0aec0', fontSize: '13px', padding: '12px 0' }}>No history recorded yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {activityLogs.map((entry, idx) => {
                    const ACTION_META = {
                      created:            { label: 'Created',            bg: '#f0fff4', border: '#9ae6b4', text: '#276749' },
                      updated:            { label: 'Updated',            bg: '#ebf8ff', border: '#bee3f8', text: '#2a69ac' },
                      deleted:            { label: 'Deleted',            bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                      timer_stopped:      { label: 'Timer Stopped',      bg: '#fffaf0', border: '#fbd38d', text: '#975a16' },
                      attachment_added:   { label: 'Attachment Added',   bg: '#e6fffa', border: '#81e6d9', text: '#234e52' },
                      attachment_removed: { label: 'Attachment Removed', bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                      comment_added:      { label: 'Comment Added',      bg: '#f5f3ff', border: '#ddd6fe', text: '#5b21b6' },
                      reminder:           { label: 'Reminder Sent',     bg: '#fffaf0', border: '#fbd38d', text: '#975a16' },
                    };
                    const { label, bg, border, text } = ACTION_META[entry.action] || { label: entry.action, bg: '#f7fafc', border: '#e2e8f0', text: '#4a5568' };
                    return (
                      <div key={entry._id || idx} style={{ border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', background: bg }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', background: border, color: text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {label}
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#2d3748' }}>
                              {entry.changedBy?.name || 'Unknown user'}
                            </span>
                          </div>
                          <span style={{ fontSize: '11px', color: '#718096' }}>
                            {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : '-'}
                          </span>
                        </div>
                        {entry.action !== 'reminder' && (
                          <div style={{ fontSize: '12px', color: '#4a5568' }}>{entry.message || '-'}</div>
                        )}
                        {entry.changes?.length > 0 && (
                          <div style={{ marginTop: '6px', borderTop: `1px dashed ${border}`, paddingTop: '6px', display: 'grid', gap: '3px' }}>
                          {entry.changes
                            .filter((ch) => ch.field !== 'worklogMemberId')
                            .map((ch, ci) => (
                              <div key={ci} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px', fontSize: '11px' }}>
                                <span style={{ color: '#4a5568', fontWeight: 600 }}>{formatHistoryFieldLabel(ch.field)}</span>
                                 <span>
                                   {entry.action === 'created' || entry.action === 'reminder' ? (
                                     <span style={{ color: '#276749' }}>
                                       {formatHistoryFieldValue(ch.field, ch.to)}
                                     </span>
                                   ) : (
                                    <>
                                      <span style={{ color: '#c53030', textDecoration: 'line-through' }}>
                                        {formatHistoryFieldValue(ch.field, ch.from)}
                                      </span>
                                      <span style={{ margin: '0 4px', color: '#718096' }}>→</span>
                                      <span style={{ color: '#276749' }}>
                                        {formatHistoryFieldValue(ch.field, ch.to)}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px', marginTop: '16px' }}>
    <div style={{ ...cardTitleStyle, marginBottom: '10px' }}>{icon && <span>{icon}</span>} {title}</div>
    {children}
  </div>
);

const TASK_STATUSES = ['Not Started', 'In Progress', 'R&D Development', 'R&D Testing', 'Final Verification', 'Complete', 'Re-open', 'On Hold', 'Invalid Issue'];
// Display labels for status values — 'Complete' is stored in DB but shown as 'Completed' in UI.
const STATUS_DISPLAY_LABEL = { Complete: 'Completed' };
const PRIORITIES = ['Low', 'Medium', 'High'];
const TASK_CATEGORIES = ['Requirement', 'Defect', 'Common Tasks'];

// Coloured badges for Status / Priority (aligned with the Tasks list palette).
const STATUS_BADGE = {
  'Not Started': { color: '#64748b', bg: 'rgba(100, 116, 139, 0.14)' },
  'In Progress': { color: '#d97706', bg: 'rgba(245, 158, 11, 0.16)' },
  'On Hold': { color: '#b45309', bg: 'rgba(180, 83, 9, 0.14)' },
  'R&D Development': { color: '#0891b2', bg: 'rgba(8, 145, 178, 0.14)' },
  'R&D Testing': { color: '#6366f1', bg: 'rgba(99, 102, 241, 0.14)' },
  'Final Verification': { color: '#ec4899', bg: 'rgba(236, 72, 153, 0.14)' },
  Complete: { color: '#059669', bg: 'rgba(16, 185, 129, 0.16)' },
  'Re-open': { color: '#dc2626', bg: 'rgba(239, 68, 68, 0.14)' },
  'Invalid Issue': { color: '#9333ea', bg: 'rgba(147, 51, 234, 0.14)' },
  // Legacy status kept so any not-yet-migrated task still renders coloured.
  Invalid: { color: '#9333ea', bg: 'rgba(147, 51, 234, 0.14)' },
};
const PRIORITY_BADGE = {
  Low: { color: '#059669', bg: 'rgba(16, 185, 129, 0.16)' },
  Medium: { color: '#d97706', bg: 'rgba(245, 158, 11, 0.16)' },
  High: { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.14)' },
};

const cardStyle = {
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: '18px 20px',
};

const cardTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '16px',
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: '10px',
};

const infoRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
  padding: '11px 0',
  borderBottom: '1px solid #f1f5f9',
};

const infoLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  color: '#64748b',
  fontSize: '14px',
};

const infoValueStyle = {
  color: '#2d3748',
  fontWeight: 700,
  fontSize: '14px',
};

const badgeStyle = ({ color, bg }) => ({
  display: 'inline-block',
  padding: '3px 12px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 700,
  color,
  background: bg,
});

const metaPillStyle = {
  fontSize: '12px',
  color: '#4a5568',
  background: '#f7fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '999px',
  padding: '4px 10px',
};

const inputStyle = {
  width: '100%',
  height: '34px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 10px',
  fontSize: '13px',
  fontFamily: 'inherit',
  color: '#2d3748',
  background: '#fff',
};

const smallActionBtnStyle = (bg, color) => ({
  border: bg === '#fff' ? '1px solid #e2e8f0' : 'none',
  background: bg,
  color,
  borderRadius: '6px',
  height: '30px',
  padding: '0 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
});

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '16px',
};

const modalCloseBtnStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: '24px',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#718096',
  padding: '2px 6px',
};

const modalCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  padding: '20px 22px',
  width: '640px',
  maxWidth: '94vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
};

const fieldLabelStyle = {
  fontSize: '13px',
  color: '#4a5568',
  marginBottom: '6px',
  fontWeight: 600,
};


const userDropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
  padding: '8px',
  zIndex: 30,
};

const selectAllBtnStyle = {
  border: '1px solid #cbd5e0',
  background: '#f7fafc',
  color: '#2d3748',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: '8px',
};

export default TaskViewPage;
